import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { ArrowLeft, BookText, Wifi, Server, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { RelayListManager } from '@/components/RelayListManager';
import { BlossomServerManager } from '@/components/BlossomServerManager';

export default function SettingsPage() {
  useSeoMeta({
    title: 'Settings — PressStr',
    description: 'Configure relays and Blossom file servers for PressStr.',
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-3 sm:px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1" />Back</Link>
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div className="flex items-center gap-1.5">
            <BookText className="w-5 h-5 text-primary" />
            <span className="font-bold text-base">Settings</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-2xl space-y-6">
        {/* Relays */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wifi className="w-5 h-5" />
              Nostr Relays
            </CardTitle>
            <CardDescription>
              Manage which relays PressStr connects to. Your relay list is published to Nostr (NIP-65, kind 10002) when logged in so other clients can find you.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RelayListManager />
          </CardContent>
        </Card>

        {/* Blossom */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Server className="w-5 h-5" />
              Blossom File Servers
            </CardTitle>
            <CardDescription>
              Blossom servers host your uploaded files (PDFs, EPUBs, cover images). When you upload, files are sent to all servers for redundancy. Your server list is published to Nostr (BUD-03, kind 10063) when logged in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BlossomServerManager />
          </CardContent>
        </Card>

        {/* About */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">About PressStr</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              PressStr v2.0.0 — A decentralized magazine and ebook client built on Nostr.
            </p>
            <p>
              Implements NIP-PS (kinds 34609, 39731, 33953) for magazines, magazine issues, and ebooks. All files hosted via Blossom.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button size="sm" variant="outline" asChild>
                <a href="https://marlowe.shakespeare.wtf" target="_blank" rel="noopener noreferrer">
                  Vibed with Marlowe <ExternalLink className="w-3 h-3 ml-1" />
                </a>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <a href="https://marlowe.shakespeare.wtf/clone?url=nostr%3A%2F%2Fnpub14rg4vrt2v374q95ezeeydu3hkdhmzglcj950mggacap4x0lv0gyq04wun7%2Frelay.ngit.dev%2Fpressstr2" target="_blank" rel="noopener noreferrer">
                  <img src="https://marlowe.shakespeare.wtf/marlowe-badge.svg" alt="Edit with Marlowe" className="h-auto" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
